import React, { forwardRef } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { TextFieldCustom } from '../helpers/formFields';
import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { formatString } from "./valueFunctions";
import { makePlural } from '../helpers/stringFormat';


// Function to create initial values dynamically
const getInitialValues = (variables) => {
    return variables.reduce((acc, variable) => {
        acc[variable.name] = { 'id': variable.id, 'value': formatNumberThsndAndDec(variable.value) };
        return acc;
    }, {});
};

const VariablesForm = forwardRef(({ variables, formActions, onSubmit }, ref) => {
    const initialValues = getInitialValues(variables);

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};

        variables.forEach(variable => {
            if (!isNumeric(values[variable.name]['value'])) {
                errors[variable.name] = {};
                errors[variable.name]['value'] = 'Must be numeric value';
            } else if (!isPositive(values[variable.name]['value'])) {
                errors[variable.name] = {};
                errors[variable.name]['value'] = 'Must be positive or zero';
            }
        })

        return errors
    };

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validate: validate,
        onSubmit: onSubmit,
    });

    const renderVariables = (variable, index) => {
        return (
            <React.Fragment key={variable.name}>
                {index === 0 &&
                    <React.Fragment>
                        <Grid item xs={6} />
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                                {formatString(variable.category)}
                            </Typography>
                        </Grid>
                    </React.Fragment>
                }
                <Grid item xs={6}>
                    <Typography variant="caption">
                        {formatString(variable.name)}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <TextFieldCustom
                        name={variable.name}
                        startAdornment={variable.unit === '€' ? variable.unit : null}
                        endAdornment={variable.unit !== '€' ? (
                            <Typography variant="caption">
                                {makePlural(variable.unit)}
                            </Typography>
                        ) : null}
                        value={formik.values[variable.name]['value']}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue(`${variable.name}.value`, formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, `${variable.name}.value`) && Boolean(getIn(formik.errors, `${variable.name}.value`))}
                        helperText={getIn(formik.touched, `${variable.name}`) && getIn(formik.errors, `${variable.name}.value`)}
                    />
                </Grid>
            </React.Fragment>
        )

    }

    const categoryList = ['general', 'labor', 'operating', 'inventory', 'investment', 'marketing and sales']

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ maxWidth: 450 }}>
            <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 2 }}>
                <Grid container rowGap={2}>
                    {variables.filter(variable => { return variable.category === 'general' }).map((variable, index) => {
                        return (
                            renderVariables(variable, index)
                        )
                    })}
                    {variables.filter(variable => { return variable.category === 'labor' }).map((variable, index) => {
                        return (
                            renderVariables(variable, index)
                        )
                    })}
                    {variables.filter(variable => { return variable.category === 'operating' }).map((variable, index) => {
                        return (
                            renderVariables(variable, index)
                        )
                    })}
                    {variables.filter(variable => { return variable.category === 'inventory' }).map((variable, index) => {
                        return (
                            renderVariables(variable, index)
                        )
                    })}
                    {variables.filter(variable => { return variable.category === 'investment' }).map((variable, index) => {
                        return (
                            renderVariables(variable, index)
                        )
                    })}
                    {variables.filter(variable => { return variable.category === 'marketing and sales' }).map((variable, index) => {
                        return (
                            renderVariables(variable, index)
                        )
                    })}
                    {variables.filter(variable => { return !categoryList.includes(variable.category) }).map((variable, index) => {
                        return (
                            renderVariables(variable, index)
                        )
                    })}
                </Grid>
            </Box>
            {formActions()}
        </Box>
    )
})

export default VariablesForm 